import {
  SpaceBackdrop,
  SpaceSkyForegroundScope,
  SpaceSkySurfaceContext,
  useIsSpaceBackgroundEnabled,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Platform, View } from "@-/com.npmjs.react-native";
import {
  PrimaryControlsStripToolbarHeightContext,
  type PrimaryControlsStripToolbarSurface,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.toolbar-height";
import {
  useIsNavigationFocused,
  usePrimaryControlsStripOpaqueChrome,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { useContext, useLayoutEffect, type ReactNode } from "react";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { ScrollEdgeFade } from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeElementContainer,
  ScrollEdgeTopInsetContext,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";

/** A vertical column fades the scene across the standard 9pt gutter beside its controls. */
const VERTICAL_PRIMARY_CONTROLS_STRIP_FADE_WIDTH = 9;

/** Pins a glass bar over the page scroller whatever placement the page gives it elsewhere. */
const GLASS_FLOATING_STYLE = {
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
  zIndex: 10,
} as const;

/**
 * The opaque toolbar row at the top of an `(app)` tab, which also tells `NavLayout` how far down
 * to place the shared header fade: at this row's bottom edge, i.e. the bottom border of its pills.
 *
 * Every tab used to hand-roll this as `<View className="h-12 bg-background">` while `NavLayout`
 * independently hard-coded the matching 48pt offset. Nothing enforced the pairing, so a tab that
 * skipped the row (Settings) left the band under the status bar unpainted and stranded the fade
 * mid-content. Publishing the height inverts that: the fade follows whatever the screen actually
 * renders, and a tab with no toolbar simply gets a fade flush under the safe area.
 *
 * The row publishes ONLY while its screen is focused. Inactive bottom tabs stay mounted, and the
 * Header fade and solid accordion chrome share the same standard control spacing; publishing
 * only the focused row prevents inactive tabs from moving another screen's fade.
 *
 * It publishes in a layout effect so the fade is never a frame behind the row on mount, and it
 * publishes the same `height` value that sizes the row, so the two cannot disagree. The controls
 * start below the shell's safe-area inset: floating rows offset inside `ScrollEdgeViewport`,
 * opaque rows follow its spacer. `NavLayout` places the fade at `insets.top + height` rather than
 * measuring the row, because a cold-start measurement can precede the safe-area layout.
 *
 * It is THE top bar of every native `(app)` screen: a screen renders exactly one (with a `title`
 * when it has nothing else to put there) instead of drawing its own heading or a look-alike row.
 * Its `surface` is published with its height, and the shell paints the status band and the
 * header fade from that, so the band, the row and the fade can never disagree. In development the
 * shell flags any `(app)` screen that renders none.
 *
 * On iOS 26 a `floating` row is not opaque at all: it floats as glass controls over the page
 * scroller, which runs beneath it and the status bar, and UIKit blurs the passing content with
 * the scroll edge effect of a system bar. It then publishes `floating` instead of a surface to
 * paint, and the shell draws neither the status band nor the fade.
 */
export function PrimaryControlsStripToolbar({
  children,
  className = "",
  fadeInset = 0,
  floating = false,
  height: heightOverride,
  hideScrollEdgeEffect = false,
  isCurrent = true,
  pointerEvents,
  stripFade = true,
  surface = "chrome",
  title,
  vertical = false,
}: {
  children?: ReactNode;
  className?: string;
  /** Leave the bottom gutter transparent where the shared header fade overlays content. */
  fadeInset?: number;
  /**
   * The page's scroller runs beneath this bar. On iOS 26 the bar then floats over the scroller
   * as bare glass controls, with no surface of its own: the content passes under it (and under
   * the status bar) and UIKit blurs it there with its scroll edge effect, as beneath a system
   * navigation bar, so the controls stay legible. The scroller declares the same chrome with
   * `glassHeaderOverlap`. Elsewhere the bar keeps the placement and surface the page gives it.
   * Per-page toggle: leave false when the page pins opaque chrome under the bar (e.g. material
   * grade tabs) so scrolling content cannot wash through those labels.
   */
  floating?: boolean;
  /** Override only for a row that is deliberately not the standard height. */
  height?: number;
  /** Hide the native backdrop beneath floating controls without changing their placement. */
  hideScrollEdgeEffect?: boolean;
  /**
   * `false` while another row has taken this one's place on its focused screen (Learn's map
   * replaces Learn's row with its own floating one). The row then declares nothing, so the shell's
   * status band and header fade follow the row that is on screen.
   */
  isCurrent?: boolean;
  pointerEvents?: "auto" | "box-none" | "box-only" | "none";
  /** Replace the native header fade with 9 points of solid background spacing while focused. */
  stripFade?: boolean;
  /**
   * `"chrome"` (default) paints the app's muted chrome, which shows the sky while the space
   * background is on; `"page"` paints `bg-background`, and `"surface"` continues a white sheet.
   */
  surface?: PrimaryControlsStripToolbarSurface;
  /** A centred native title, for screens with no other bar content (web shows its own header). */
  title?: string;
  /** Reserve a right-side column instead of a top row. */
  vertical?: boolean;
}) {
  const registry = useContext(PrimaryControlsStripToolbarHeightContext);
  const topInset = useContext(ScrollEdgeTopInsetContext);
  const isFocused = useIsNavigationFocused();
  usePrimaryControlsStripOpaqueChrome(!stripFade);
  const { diameter, height: gridHeight } = useToolbarGridMetrics();
  const hasSolidSpacing = !stripFade && Platform.OS !== "web";
  // Standard rows already reserve 9px above and below every control.
  const solidSpacing = hasSolidSpacing && heightOverride !== undefined ? 9 : 0;
  const height = (heightOverride ?? gridHeight) + solidSpacing;
  const hasInsetFade = stripFade && !vertical && Platform.OS !== "web" && fadeInset > 0;
  // A landscape column has no shared `PrimaryControlsStripFade` below it, so it owns the fade on its
  // content-facing edge — the side opposite the device edge it hugs. The column is transparent
  // over the tab scene, so the content dissolves into the scene background rather than muted.
  const hasVerticalFade = stripFade && vertical && Platform.OS !== "web";

  const isChrome = surface === "chrome";
  const surfaceClassName = isChrome
    ? "bg-muted"
    : surface === "surface"
      ? "bg-surface"
      : "bg-background";
  const isSpaceBackgroundEnabled = useIsSpaceBackgroundEnabled();
  const isGlassFloating =
    floating && !vertical && (isScrollEdgeEffectSupported || Platform.OS === "android");

  useLayoutEffect(() => {
    if (!isFocused || !isCurrent) {
      return;
    }
    return registry.publish(vertical ? 0 : height, surface, isGlassFloating);
  }, [height, isCurrent, isFocused, isGlassFloating, registry, surface, vertical]);

  if (isGlassFloating) {
    return (
      <ScrollEdgeElementContainer
        className={`overflow-visible ${className ?? ""}`}
        // The whole bar shapes the effect, title and text tabs included, not only its glass.
        coversBounds
        hideScrollEdgeEffect={hideScrollEdgeEffect}
        pointerEvents={pointerEvents}
        style={[GLASS_FLOATING_STYLE, { height, top: topInset }]}
      >
        {title === undefined ? null : (
          <PrimaryControlsStripToolbarTitle isOverSky={false} title={title} />
        )}
        {/* The controls float over page content, never over the sky or an opaque bar. */}
        <SpaceSkySurfaceContext value={false}>{children}</SpaceSkySurfaceContext>
      </ScrollEdgeElementContainer>
    );
  }

  return (
    <View
      className={`z-10 overflow-visible ${hasInsetFade || vertical ? "" : surfaceClassName} ${solidSpacing === 9 ? "pb-2.25" : ""} ${className ?? ""}`}
      pointerEvents={pointerEvents}
      style={vertical ? { width: diameter + 18 } : { height }}
    >
      {hasInsetFade ? (
        <View
          className={`absolute inset-x-0 top-0 ${surfaceClassName}`}
          pointerEvents="none"
          style={{ bottom: fadeInset }}
        >
          {isChrome ? <SpaceBackdrop /> : null}
        </View>
      ) : vertical || !isChrome ? null : (
        <SpaceBackdrop />
      )}
      {title === undefined || vertical || Platform.OS === "web" ? null : (
        <PrimaryControlsStripToolbarTitle
          isOverSky={isChrome && isSpaceBackgroundEnabled}
          title={title}
        />
      )}
      {hasVerticalFade ? (
        <View
          className="absolute inset-y-0 right-full"
          pointerEvents="none"
          style={{ width: VERTICAL_PRIMARY_CONTROLS_STRIP_FADE_WIDTH }}
        >
          <ScrollEdgeFade
            edge="right"
            height={VERTICAL_PRIMARY_CONTROLS_STRIP_FADE_WIDTH}
            surfaceColorVariable={surface === "surface" ? "--color-surface" : "--color-background"}
          />
        </View>
      ) : null}
      {vertical ? (
        <View className="flex-1" pointerEvents={pointerEvents}>
          {children}
        </View>
      ) : (
        // A chrome row paints the sky when the space background is on; its controls float on it.
        <SpaceSkySurfaceContext value={isChrome}>{children}</SpaceSkySurfaceContext>
      )}
    </View>
  );
}

/** Centred in the controls lane, clear of the corner controls on both sides. */
function PrimaryControlsStripToolbarTitle({
  isOverSky,
  title,
}: {
  isOverSky: boolean;
  title: string;
}) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <View
      className="absolute inset-x-0 top-0 items-center pt-2.25"
      pointerEvents="none"
      style={{ paddingHorizontal: diameter + 18 }}
    >
      <View className="justify-center" style={{ height: diameter }}>
        <SpaceSkyForegroundScope enabled={isOverSky}>
          <Text
            accessibilityRole="header"
            adjustsFontSizeToFit
            className="text-center text-xl font-bold text-foreground"
            minimumFontScale={0.6}
            numberOfLines={1}
          >
            {title}
          </Text>
        </SpaceSkyForegroundScope>
      </View>
    </View>
  );
}
